import './AppShell.css';

import { ReactNode, useState } from 'react';
import { IonPage, IonRouterOutlet } from '@ionic/react';
import Header from '../PageLayout/components/Header';
import Sidebar from '../../components/Sidebar/Sidebar';
import SupportModal from '../../components/SupportModal/SupportModal';
import OfflinePanel from '../../components/OfflinePanel/OfflinePanel';
import Loading from '../../components/Loading/Loading';
import RouteTransitionOverlay from '../../components/RouteTransitionOverlay/RouteTransitionOverlay';
import GuidePanel from '../../components/Guide/GuidePanel/GuidePanel';
import GuideTour from '../../components/Guide/GuideTour/GuideTour';
import GuideStartBanner from '../../components/Guide/GuideStartBanner/GuideStartBanner';
import GuideLauncher from '../../components/Guide/GuideLauncher/GuideLauncher';
import QuickBookmarksPanel from '../../components/QuickBookmarks/QuickBookmarksPanel';
import QuickBookmarkReminder from '../../components/QuickBookmarks/QuickBookmarkReminder';
import { useUserState } from '../../hooks/context/useUserState';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useFormTokenChainContext } from '../../hooks/context/useFormTokenChainContext';
import { useLogout } from '../../api/hooks/useLogout';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { useTranslate } from '../../hooks/context/useTranslate';
import useDocumentTitle from '../../hooks/useDocumentTitle';

// The persistent app frame for logged-in routes: one Header + one Sidebar that never remount, with the
// routed pages rendered into the nested IonRouterOutlet. Lives inside LoggedInLayout's providers so it
// can read menuItems / current dataset / token chain directly instead of threading props per page.
const AppShell = ({ children }: { children: ReactNode }) => {
  const {
    state: { username, menuItems },
  } = useUserState();
  const { dataset } = useDatasetContext();
  const { getActualTokenFromUrl, setSmallExpirationTimeToTokenChain } =
    useFormTokenChainContext();
  const { logout, isPending } = useLogout();
  const handleApiError = useApiErrorHandling();
  const { t } = useTranslate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  useDocumentTitle();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      handleApiError(e);
    }
  };

  // Mirrors the old withDatasetPageLayout behaviour: refresh the active form token's expiry on menu nav.
  const handleMenuItemClick = () => {
    const token = getActualTokenFromUrl();
    if (token) {
      setSmallExpirationTimeToTokenChain(token.id);
    }
  };

  return (
    <IonPage id="main">
      <SupportModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
      />
      <OfflinePanel />
      {isPending && <Loading message={t('loggingOut')} />}
      <Header
        onPopoverOpen={() => setIsModalOpen(true)}
        onLogout={handleLogout}
        filter={<div slot="end" className="header-filter-slot" />}
      />
      <div className="app-shell-body">
        {username && menuItems && menuItems.length !== 0 && (
          <Sidebar
            menuItems={menuItems}
            peersets={dataset?.peersets}
            onMenuItemClick={handleMenuItemClick}
          />
        )}
        <div className="page-outlet">
          <IonRouterOutlet>{children}</IonRouterOutlet>
          <RouteTransitionOverlay />
          {/* AIR-410 Průvodce: agenda help panel and first-login nudge overlay only the content area. */}
          <GuidePanel />
          <QuickBookmarksPanel />
          <QuickBookmarkReminder />
          <GuideStartBanner />
          <GuideLauncher />
        </div>
      </div>
      <GuideTour />
    </IonPage>
  );
};

export default AppShell;
